// components/LatestCommunications.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, AlertCircle, Settings, Play, Pause } from 'lucide-react';
import useCommunicationsParams from '../hooks/useCommunicationsParams';

const LatestCommunications = () => {
  const { communications, loading, error } = useCommunicationsParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true); // 🔹 État du bouton Play/Pause

  useEffect(() => {
    if (!loading && !error && communications && communications.length > 0 && isPlaying) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex === communications.length - 1 ? 0 : prevIndex + 1
        );
      }, 7000);
      return () => clearInterval(timer);
    }
  }, [loading, error, communications, isPlaying]);

  const togglePlayPause = () => setIsPlaying((prev) => !prev);

  // --- États de chargement / erreur / vide ---
  if (loading || error || !communications || communications.length === 0) {
    const state = loading ? 'loading' : error ? 'error' : 'empty';
    const bgColor =
      state === 'error'
        ? 'bg-gradient-to-r from-red-500 to-red-600'
        : state === 'loading'
        ? 'bg-gray-200 animate-pulse'
        : 'bg-gradient-to-r from-gray-400 to-gray-500';
    const content =
      state === 'loading'
        ? null
        : state === 'error'
        ? (
          <div className="text-center text-white text-sm">
            <AlertCircle size={20} className="mx-auto mb-1" />
            <div className="font-semibold">Erreur de chargement</div>
            <div className="opacity-90 text-xs">Impossible de récupérer les communications</div>
          </div>
        ) : (
          <div className="text-center text-white text-sm">
            <Settings size={20} className="mx-auto mb-1" />
            <div className="font-semibold">Aucune communication</div>
            <div className="opacity-90 text-xs">Configurez vos messages dans les paramètres</div>
          </div>
        );

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-[#246990]" />
            <h2 className="text-lg font-bold text-[#246990]">Dernières Communications</h2>
          </div>
        </div>
        <div className={`h-32 relative overflow-hidden ${bgColor} p-4 rounded-lg flex items-center justify-center`}>
          {content}
        </div>
      </div>
    );
  }

  // --- Affichage normal ---
  const currentCommunication = communications[currentIndex];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-[#246990]" />
          <h2 className="text-lg font-bold text-[#246990]">Dernières Communications</h2>
        </div>

        {/* 🔹 Petit bouton Play/Pause discret */}
        <button
          onClick={togglePlayPause}
          className="p-1.5 rounded-full bg-[#246990]/10 hover:bg-[#246990]/20 text-[#246990] transition-colors"
          title={isPlaying ? 'Mettre en pause' : 'Reprendre'}
        >
          {isPlaying ? <Pause size={14} /> : <Play size={14} />}
        </button>
      </div>

      <div className="h-32 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={`comm-${currentIndex}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className={`bg-gradient-to-r ${currentCommunication.color_gradient} p-4 rounded-lg text-white text-sm h-full flex flex-col justify-between`}
          >
            <div>
              <div className="font-semibold mb-2">{currentCommunication.source}</div>
              <div className="opacity-90 leading-relaxed line-clamp-3">{currentCommunication.message}</div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LatestCommunications;
